import React, { type MouseEvent, type ReactNode } from 'react';
import type { Store } from 'redux';
import type { AnalyticsEventPayload, UIAnalyticsEvent } from '@atlaskit/analytics-next';
import InfoIcon from '@atlaskit/icon/core/migration/information--info';
import { Box, xcss } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';
import { token } from '@atlaskit/tokens';
import { toFieldCategory } from '@atlassian/jira-common-rest/src/internal/simplified/1.0/projects/id/issuetypes/id/types.tsx';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import {
	APPROVERS_LIST_CF_TYPE,
	ATTACHMENT_TYPE as ATTACHMENT_FIELD_TYPE,
	SUMMARY_TYPE as SUMMARY_FIELD_TYPE,
} from '@atlassian/jira-platform-field-config/src/index.tsx';
import {
	DESCRIPTION_FIELD_TYPE,
	ENVIRONMENT_FIELD_TYPE,
	TEXT_AREA_CF_TYPE,
} from '@atlassian/jira-project-settings-apps-common/src/consts.tsx';
import { getNextgenSettingsIssueTypesUrl } from '@atlassian/jira-project-settings-apps-common/src/urls.tsx';
import { SWIFT_VERSION_M2 } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/constants.tsx';
import {
	fetchAvailableLayoutItems,
	fetchMoreAvailableLayoutItems,
} from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/services/fetch-available-items/index.tsx';
import type {
	FieldCard,
	GetActiveFieldHeaderInfo,
	GetFieldTooltipContent,
	IssueLayoutBreadcrumbConfig,
	ProductFeatures,
} from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/types.tsx';
import type { EmptyStateComponentProps } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/types/empty-state.tsx';
import {
	allowFieldLayoutItem,
	type AssociatedField,
	type FieldCategoryConfiguration,
} from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/types/layout-items.tsx';
import { FieldToolbarNoAction } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/ui/droppable-list/field-toolbar-no-action/index.tsx';
import EmptyState from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/ui/empty-state/index.tsx';
import { getFieldCard as getDefaultFieldCard } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/ui/field-active/get-field-card.tsx';
import {
	// eslint-disable-next-line jira/styled/no-styled-import-alias
	InfoMessageNotDraggable as FieldInfoMessageNotDraggable,
	// eslint-disable-next-line jira/styled/no-styled-import-alias
	InfoMessageWrapper as FieldInfoMessageWrapper,
} from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/ui/field-active/styled.tsx';
import { FieldTooltipContent } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/ui/field-tooltip-content/main.tsx';
import { getMessageFlags } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/ui/field-tooltip-content/utils.tsx';
import { SidebarContainer } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/ui/sidebar/sidebar-container/index.tsx';
import StatusField from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/ui/status-field/index.tsx';
import {
	NonShrinkableFooter,
	Scrollable,
} from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/ui/styled.tsx';
import { mergeGetActiveFieldHeaderInfo } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/utils/active-field-header-info/merge.tsx';
import { requiredFieldActiveFieldHeaderInfo } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/utils/active-field-header-info/required-field.tsx';
import { fieldDefaultValueValidator } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/utils/validation/field/default-value/index.tsx';
import { nameMustBeNotEmpty } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/utils/validation/field/name-and-description/index.tsx';
import { optionsMustBeUniquePerParent } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/utils/validation/field/options/index.tsx';
import { composeValidators } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/utils/validation/field/utils.tsx';
import { validateIssueType } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/utils/validation/issue-type/index.tsx';
import { ecosystemAppFieldWarning } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/ui/ecosystem/restricted-forge-field-warnings/main.tsx';
import { RestrictedFieldWarnings } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/ui/restricted-field-warnings/main.tsx';
import { messages as restrictedFieldMessages } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/ui/restricted-field-warnings/messages.tsx';
import { getRestrictions } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/ui/restricted-field-warnings/utils.tsx';
import {
	editIssueTypeMessages,
	editIssueTypeMessagesIssueTermRefresh,
	fieldsEditorMessages,
	navigationMessages,
	requiredFieldMessages,
	requiredFieldMessagesIssueTermRefresh,
} from '@atlassian/jira-project-settings-product-features-jsm-common/src/common/messages.tsx';
import { DuplicateFormLinkedFieldsWarningDialogWithErrorBoundary as DuplicateFormLinkedFieldsWarningDialog } from '@atlassian/jira-project-settings-product-features-jsm-common/src/ui/duplicate-form-linked-fields-warning-dialog/index.tsx';
import { MultilineFieldConfiguration as MultilineFieldConfigurationForJsmPortal } from '@atlassian/jira-project-settings-product-features-jsm-common/src/ui/field-configuration/multiline-without-mentions/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { fieldAliasDescriptionMustBeShortEnough } from './common/utils/field-validator/index.tsx';
import { doesJsmTmpSupportIssueTypeHierarchy, getRequestFieldCategoryId } from './constants.tsx';
import {
	addIssueTypeMessages,
	deleteFieldMessages,
	fieldCategories,
	fieldWarnings,
} from './messages.tsx';
import { fetchLayoutConfiguration } from './services/fetch-layout-configuration/index.tsx';
import {
	saveChangesService,
	saveChangesServiceWithProformaContext,
} from './services/save-changes/index.tsx';
import { MainContent } from './ui/main-content-new/index.tsx';
import { MainHeader } from './ui/main-header-new/index.tsx';
import { HiddenInContextWarning } from './ui/restricted-field-warnings/index.tsx';
import { JsmTmpSidebar } from './ui/sidebar/index.tsx';
import { ViewButtonWrapper } from './ui/view-button/index.tsx';

const fieldsDocumentationUrl =
	'https://confluence.atlassian.com/display/SERVICEDESKCLOUD/Customize+an+issue%27s+fields+in+next-gen+projects';
const fieldsDocumentationInProductHelpArticleId = '5xtQIjEWw12ExbUuX2Wk7h';
const portalDocumentationInProductHelpArticleId = '4S9HZghmTHRR7tUGV44eiV';

const getServiceDeskRequestCategory = (): FieldCategoryConfiguration => ({
	name: getRequestFieldCategoryId(),
	displayName: fieldCategories.alwaysShowTitleRequestForm,
	emptyStateText: fieldCategories.primaryEmptyMessage,
	categoryEmptyState: (props: EmptyStateComponentProps) => (
		<EmptyState emptyStateText={fieldCategories.primaryEmptyMessage} {...props} />
	),
	whatIsThis: {
		buttonLabel: fieldCategories.whatIsThis,
		content: fieldCategories.whatArePrimaryFieldsForInProductHelp,
		linkToDocumentationText: fieldCategories.linkToInProductHelpTextPortal,
		linkToInProductHelpArticleId: portalDocumentationInProductHelpArticleId,
	},
	allowRequiredField: true,
	allowFieldAliasEditing: true,
	allowFieldAlias: true,
	allowedLayoutItems: [],
});

const getServiceDeskEmailRequestCategory = (): FieldCategoryConfiguration => ({
	name: getRequestFieldCategoryId(),
	displayName: fieldCategories.emailFieldsTitle,
	emptyStateText: fieldCategories.primaryEmptyMessage,
	whatIsThis: {
		buttonLabel: fieldCategories.whatIsThis,
		content: fieldCategories.whatAreEmailFields,
		linkToDocumentationText: fieldCategories.linkToDocumentationText,
	},
	allowRequiredField: false,
	allowFieldAlias: true,
	allowFieldAliasEditing: false,
	allowedLayoutItems: [
		allowFieldLayoutItem(SUMMARY_FIELD_TYPE),
		allowFieldLayoutItem(DESCRIPTION_FIELD_TYPE),
		allowFieldLayoutItem(ATTACHMENT_FIELD_TYPE),
	],
	// Since this field category is only used for the Email Request type it will only override properties in the
	// `summary`, `description` and `attachment` fields in Email Request Types
	overriddenFields: [
		{
			key: 'summary',
			required: true,
			category: getRequestFieldCategoryId(),
			// @ts-expect-error - TS2739 - Type '{ canModifyRequired: false; categoriesWhitelist: string[]; editable: false; removable: false; canAssociateInSettings: true; }' is missing the following properties from type 'Readonly<Readonly<{ editable: boolean; removable: boolean; categoriesWhitelist: string[]; canAssociateInSettings: boolean; deletable: boolean; }> & { canModifyOptions: boolean; canModifyDefaultValue: boolean; canModifyPropertyConfiguration: boolean; canModifyRequired: boolean; }>': deletable, canModifyOptions, canModifyDefaultValue, canModifyPropertyConfiguration
			operations: {
				canModifyRequired: false,
				categoriesWhitelist: [getRequestFieldCategoryId()],
				editable: false,
				removable: false,
				canAssociateInSettings: true,
			},

			sortable: false,
		},
		{
			key: 'description',
			required: false,
			category: getRequestFieldCategoryId(),
			// @ts-expect-error - TS2739 - Type '{ canModifyRequired: false; categoriesWhitelist: string[]; editable: false; removable: false; canAssociateInSettings: true; }' is missing the following properties from type 'Readonly<Readonly<{ editable: boolean; removable: boolean; categoriesWhitelist: string[]; canAssociateInSettings: boolean; deletable: boolean; }> & { canModifyOptions: boolean; canModifyDefaultValue: boolean; canModifyPropertyConfiguration: boolean; canModifyRequired: boolean; }>': deletable, canModifyOptions, canModifyDefaultValue, canModifyPropertyConfiguration
			operations: {
				canModifyRequired: false,
				categoriesWhitelist: [getRequestFieldCategoryId()],
				editable: false,
				removable: false,
				canAssociateInSettings: true,
			},

			sortable: false,
		},
		{
			key: 'attachment',
			required: false,
			category: getRequestFieldCategoryId(),
			// @ts-expect-error - TS2739 - Type '{ canModifyRequired: false; categoriesWhitelist: string[]; editable: false; removable: false; canAssociateInSettings: true; }' is missing the following properties from type 'Readonly<Readonly<{ editable: boolean; removable: boolean; categoriesWhitelist: string[]; canAssociateInSettings: boolean; deletable: boolean; }> & { canModifyOptions: boolean; canModifyDefaultValue: boolean; canModifyPropertyConfiguration: boolean; canModifyRequired: boolean; }>': deletable, canModifyOptions, canModifyDefaultValue, canModifyPropertyConfiguration
			operations: {
				canModifyRequired: false,
				categoriesWhitelist: [getRequestFieldCategoryId()],
				editable: false,
				removable: false,
				canAssociateInSettings: true,
			},

			sortable: false,
		},
	],
});

// This category gives support to multi line fields in JSD
// They are visible in bento but are not in the sidebar or visible to help seekers so they require a new category
// https://hello.atlassian.net/wiki/spaces/VPORT/pages/582718933/JSD+SWIFT+spec+-+Multi-line+field+and+Buckets
const getServiceDeskMultiLineCategory = (): FieldCategoryConfiguration => ({
	name: toFieldCategory('content'),
	displayName: fieldCategories.descriptionTitle,
	emptyStateText: fieldCategories.descriptionEmptyTitle,
	categoryEmptyState: (props: EmptyStateComponentProps) => (
		<EmptyState emptyStateText={fieldCategories.descriptionEmptyTitle} {...props} />
	),
	whatIsThis: {
		buttonLabel: fieldCategories.whatIsThis,
		content: fg('itsm-issue-renaming')
			? fieldCategories.whatAreDescriptionFieldsForInProductHelpIssueTermRefresh
			: fieldCategories.whatAreDescriptionFieldsForInProductHelp,
		linkToDocumentationText: fg('itsm-issue-renaming')
			? fieldCategories.linkToInProductHelpTextIssueTermRefresh
			: fieldCategories.linkToInProductHelpText,
		linkToInProductHelpArticleId: fieldsDocumentationInProductHelpArticleId,
	},
	allowRequiredField: false,
	allowFieldAliasEditing: false,
	allowFieldAlias: true,
	allowedLayoutItems: [allowFieldLayoutItem(TEXT_AREA_CF_TYPE)],
});

const getServiceDeskPrimaryCategory = (): FieldCategoryConfiguration => ({
	name: toFieldCategory('primary'),
	displayName: fieldCategories.primaryTitle,
	emptyStateText: fieldCategories.secondaryEmptyMessage,
	categoryEmptyState: (props: EmptyStateComponentProps) => (
		<EmptyState emptyStateText={fieldCategories.secondaryEmptyMessage} {...props} />
	),
	whatIsThis: {
		buttonLabel: fieldCategories.whatIsThis,
		content: fg('itsm-issue-renaming')
			? fieldCategories.whatAreSecondaryFieldsWithDividerForInProductHelpIssueTermRefresh
			: fieldCategories.whatAreSecondaryFieldsWithDividerForInProductHelp,
		linkToDocumentationText: fg('itsm-issue-renaming')
			? fieldCategories.linkToInProductHelpTextIssueTermRefresh
			: fieldCategories.linkToInProductHelpText,
		linkToInProductHelpArticleId: fieldsDocumentationInProductHelpArticleId,
	},
	renderBeforeItems: StatusField,
	allowRequiredField: false,
	allowFieldAliasEditing: false,
	allowFieldAlias: false,
	allowedLayoutItems: [],
});

const getServiceDeskSecondaryCategory = (): FieldCategoryConfiguration => ({
	name: toFieldCategory('secondary'),
	displayName: fieldCategories.hideWhenEmptyFields,
	dividerText: fieldCategories.showMoreDividerText,
	emptyStateText: fg('itsm-issue-renaming')
		? fieldCategories.hideFieldsBelowCategoryEmptyMessageWithDraggableDividerIssueTermRefresh
		: fieldCategories.hideFieldsBelowCategoryEmptyMessageWithDraggableDivider,
	categoryEmptyState: (props: EmptyStateComponentProps) => (
		<EmptyState
			emptyStateText={
				fg('itsm-issue-renaming')
					? fieldCategories.hideFieldsBelowCategoryEmptyMessageWithDraggableDividerIssueTermRefresh
					: fieldCategories.hideFieldsBelowCategoryEmptyMessageWithDraggableDivider
			}
			{...props}
		/>
	),
	allowRequiredField: false,
	allowFieldAliasEditing: false,
	allowFieldAlias: false,
	allowedLayoutItems: [],
});

const getServiceDeskFieldCategories = (): FieldCategoryConfiguration[] => [
	getServiceDeskRequestCategory(),
	getServiceDeskMultiLineCategory(),
	getServiceDeskPrimaryCategory(),
	getServiceDeskSecondaryCategory(),
];

const getServiceDeskEmailedRequestTypeFieldCategories = (): FieldCategoryConfiguration[] => [
	getServiceDeskEmailRequestCategory(),
	getServiceDeskMultiLineCategory(),
	getServiceDeskPrimaryCategory(),
	getServiceDeskSecondaryCategory(),
];

function getApprovalFieldWarning(associatedField: AssociatedField, intl: IntlShape): ReactNode {
	if (associatedField.type === APPROVERS_LIST_CF_TYPE) {
		// When the admin configures an approval where the approvers are set by the customer -
		// an approver list field is added to SWIFT in the customer portal bucket.
		// Approver fields are special people fields used for storing approvers.
		const approvalConfig = associatedField.configuration
			? associatedField.configuration['jsd.field.approval.config']
			: undefined;
		if (
			approvalConfig !== undefined &&
			approvalConfig !== null &&
			typeof approvalConfig === 'object'
		) {
			// @ts-expect-error - TS2339 - Property 'statusName' does not exist on type '{}'.
			const { statusName } = approvalConfig;
			// We always expect statusName to be available as a human readable string, if it's not we want to throw an error
			// here as a clear signal that something has gone wrong.
			if (Boolean(statusName) === false || typeof statusName !== 'string') {
				throw new Error(
					'Expected approvalConfig.statusName to be a valid human readable string from the backend, instead got undefined or an object',
				);
			}
			return (
				<div key={`approver-list-information-for-${String(statusName)}`}>
					{intl.formatMessage(fieldWarnings.approversListInformation, {
						approvalStatus: statusName,
					})}
				</div>
			);
		}
	}
	return null;
}

const fieldValidatorIncludingConfiguration = composeValidators([
	nameMustBeNotEmpty,
	fieldAliasDescriptionMustBeShortEnough,
	fieldDefaultValueValidator,
	optionsMustBeUniquePerParent,
]);

const breadcrumbConfig: IssueLayoutBreadcrumbConfig = {
	getHref: getNextgenSettingsIssueTypesUrl,
	message: navigationMessages.issueTypesLink,
	type: 'issue-type',
};

const getFieldTooltipContent: GetFieldTooltipContent = ({
	associatedField: field,
	fieldCreator,
	isRequirabilityDisplayed,
	isEditable,
	isAppField,
}) => {
	const messageFlags = getMessageFlags({
		uneditable: !isEditable,
		unrequirable: isRequirabilityDisplayed && !field.operations.canModifyRequired,
		unsortable: field.sortable !== undefined && field.sortable === false,
		unremovable: !field.operations.removable,
	});

	if (!messageFlags) {
		return null;
	}

	return (
		<FieldTooltipContent {...messageFlags} isAppField={isAppField} fieldCreator={fieldCreator} />
	);
};

const stopPropagation = (e: MouseEvent<HTMLElement>) => e.stopPropagation();

const DiscoveryInfoIcon = () => (
	<Box xcss={iconWrapperStyles}>
		<InfoIcon color={token('color.icon.discovery')} label="" spacing="spacious" />
	</Box>
);

export const approvalMessage: GetActiveFieldHeaderInfo = ({ associatedField, intl }) => {
	const approvalFieldWarning = getApprovalFieldWarning(associatedField, intl);
	if (approvalFieldWarning != null) {
		return {
			longText: (
				<FieldInfoMessageWrapper>
					<FieldInfoMessageNotDraggable onMouseDown={stopPropagation}>
						<SectionMessage appearance="discovery" icon={DiscoveryInfoIcon}>
							{approvalFieldWarning}
						</SectionMessage>
					</FieldInfoMessageNotDraggable>
				</FieldInfoMessageWrapper>
			),
		};
	}
	return undefined;
};

export const restrictedFieldWarning: GetActiveFieldHeaderInfo = ({
	associatedField,
	layoutOwners,
	intl,
}) => {
	const restrictions =
		// there could be no restrictions for scoped fields
		// because such fields will be automatically linked with the issuetype on association
		associatedField.global &&
		getRestrictions(associatedField.availability, layoutOwners, associatedField.custom);

	if (restrictions) {
		return {
			icon: {
				type: 'warning',
				label: restrictedFieldMessages.restrictedFieldIconLabel,
			},
			longText: (
				<RestrictedFieldWarnings
					intl={intl}
					fieldKey={associatedField.key}
					hiddenInContext={restrictions.hiddenInContext}
					hiddenInConfigurationsWithIssueTypes={restrictions.hiddenInConfigurationsWithIssueTypes}
					HiddenInContextComponent={HiddenInContextWarning}
				/>
			),
		};
	}

	return undefined;
};

const getFieldCard = (fieldType: string): FieldCard => {
	switch (fieldType) {
		/**
		 * Environment field has turned off default value because it's not supported in JSM CMP.
		 * But this code is JSM TMP. Perhaps it should be enabled again?
		 * See https://stash.atlassian.com/projects/JIRACLOUD/repos/jira-frontend/commits/0733055f2d2f4734349a99f0c1fa0318455c507b#src/packages/project-settings/issue-type-page-product-features/src/common/utils/service-desk/index.js
		 */
		case ENVIRONMENT_FIELD_TYPE:
			return {
				isExpandable: () => false,
				ConfigurationComponent: () => null,
			};
		case TEXT_AREA_CF_TYPE:
		case DESCRIPTION_FIELD_TYPE:
			return {
				isExpandable: (operations) => operations.canModifyDefaultValue,
				ConfigurationComponent: MultilineFieldConfigurationForJsmPortal,
			};
		default:
			return getDefaultFieldCard(fieldType);
	}
};

/**
 * Get product features for a team-managed JSM project.
 */
export const getServiceDeskFeatures = (
	store?: Store,
	bottomControlsIsDirty?: boolean,
	bottomControlsIsSaving?: boolean,
	bottomControlsDiscardChanges?: () => void,
	createAnalyticsEvent?: (payload: AnalyticsEventPayload) => UIAnalyticsEvent,
): ProductFeatures => ({
	fieldAlias: true,
	issueTypeHierarchy: doesJsmTmpSupportIssueTypeHierarchy(),
	issueTypeHelpText: true,
	allowRequiringFields: true,
	addNewFieldsToBottom: true,
	workflowEditor: true,
	issueRestriction: false,
	fieldCategoryConfiguration: getServiceDeskFieldCategories(),
	issueTypeTemplates: [],
	addIssueTypeMessages,
	deleteFieldMessages,
	navigationMessages,
	editIssueTypeMessages: fg('itsm-issue-renaming')
		? { ...editIssueTypeMessages, ...editIssueTypeMessagesIssueTermRefresh }
		: editIssueTypeMessages,
	fieldsEditorMessages,
	requiredFieldMessages: fg('itsm-issue-renaming')
		? { ...requiredFieldMessages, ...requiredFieldMessagesIssueTermRefresh }
		: requiredFieldMessages,
	fieldsDocumentationUrl,
	getActiveFieldHeaderInfo: mergeGetActiveFieldHeaderInfo(
		requiredFieldActiveFieldHeaderInfo,
		approvalMessage,
		restrictedFieldWarning,
		ecosystemAppFieldWarning,
	),
	BottomProductControl: ViewButtonWrapper,
	saveChangesService: store
		? saveChangesServiceWithProformaContext(store, createAnalyticsEvent)
		: saveChangesService,
	AvailableLayoutItemsSidebar: JsmTmpSidebar,
	fetchAvailableItemsService: fetchAvailableLayoutItems('ISSUE_VIEW'),
	fetchMoreAvailableItemsService: fetchMoreAvailableLayoutItems('ISSUE_VIEW'),
	fetchLayoutConfiguration: fetchLayoutConfiguration(
		getServiceDeskFieldCategories(),
		getServiceDeskEmailedRequestTypeFieldCategories(),
	),
	ConfirmBeforeSave: DuplicateFormLinkedFieldsWarningDialog,
	MainContent,
	MainHeader,
	Scrollable,
	Footer: NonShrinkableFooter,
	getFieldCard,
	validateField: fieldValidatorIncludingConfiguration,
	getFieldTooltipContent,
	validateIssueType: ({ issueDataState, existingIssueTypeNames }) =>
		validateIssueType(
			{},
			{},
			issueDataState.name.value,
			existingIssueTypeNames,
			issueDataState.helptext.value,
			issueDataState.description.value,
		),
	validateIssueDataCards: () => [],
	issueLayoutBreadcrumbConfig: breadcrumbConfig,
	issueLayoutType: 'customer',
	metricsConfig: {
		pageLoad: {
			key: 'project-settings.issue-type.jsm-tmp',
		},
	},
	feedbackCollectorConfig: {
		feedbackGroupId: 'project-settings.issue-type',
		source: 'jsm-tmp-request-type-config',
	},
	getScreenId: () => undefined, // TMP projects don’t use screens
	SidebarContainer,
	swiftVersionForAnalytics: SWIFT_VERSION_M2,
	FieldToolbarNoAction,
	bottomControlsIsDirty,
	bottomControlsIsSaving,
	bottomControlsDiscardChanges,
});

const iconWrapperStyles = xcss({
	width: '24px',
});
